import {AbstractControl, ValidatorFn} from '@angular/forms';
import {ValidationType} from '../enum/validation-type';
import {maybeEquals} from '~shared/utils/utils';
import {interpolate} from '~shared/utils/text';
import {Moment} from 'moment/moment';

/*
// Validation rule example from angular documentation.

export function forbiddenNameValidator(nameRe: RegExp): ValidatorFn {
  return (control: AbstractControl): {[key: string]: any} => {
    const forbidden = nameRe.test(control.value);
    return forbidden ? {'forbiddenName': {value: control.value}} : null;
  };
}
*/
export const confirmPasswordValidator = function (): ($value: string, $formData: {
    newPassword: string
}) => (IValidationRuleError | null) {
    return ($value, $formData) => {
        if ($formData.newPassword !== $value) {
            return {value: $value, message: 'Password does not match'};
        }
        return null;
    };
};

const normalizeValueWithLength = ($value: any): any => {
    if ($value == null) {
        return $value;
    }

    switch (typeof $value) {
        case 'number':
            return $value.toString();
        case 'object':
            return $value.hasOwnProperty('length') ? $value : Object.keys($value);
        default:
            return $value.toString ? $value.toString() : undefined;
    }
};

const normalizeValueAsString = ($value: any): string => {
    if ($value == null) {
        return $value;
    }

    if (typeof $value === 'number') {
        return $value.toString();
    }

    return $value.toString ? $value.toString() : undefined;
};

export type IValidationRuleError = {
    value: any,
    message: string,
};

export abstract class ValidationRule {

    protected static plural($message: string, $value: number): string {
        if (/\|/.test($message)) {
            return $message.split('|', 2)[$value === 0 || $value === 1 ? 0 : 1];
        }
        return $message;
    }

    protected constructor(
        public readonly name: string
    ) {
    }

    public abstract get validator(): ValidatorFn;
}

export abstract class ValidationRuleWithMessage extends ValidationRule {

    public abstract validate($value: any): boolean;

    public abstract getMessageForValue($value: any): string;

    public get validator(): ValidatorFn {
        return ($control: AbstractControl): { [key: string]: IValidationRuleError } | null => {
            return this.validate($control.value)
                ? null
                : {
                    [this.name]: {
                        value: $control.value,
                        message: this.getMessageForValue($control.value)
                    }
                };
        };
    }
}

export class SizeRule extends ValidationRuleWithMessage {
    private readonly minMessage: string;
    private readonly maxMessage: string;

    constructor(
        public readonly min: number,
        public readonly max: number = Number.POSITIVE_INFINITY,
        $minMessage: string = 'Value must equal or longer then {{ min }}.',
        $maxMessage: string = 'Value must not exceed {{ max }}.'
    ) {
        super(ValidationType.SIZE);

        this.minMessage = ValidationRule.plural(
            interpolate($minMessage, this),
            this.min
        );

        this.maxMessage = ValidationRule.plural(
            interpolate($maxMessage, this),
            this.max
        );
    }

    public validate($value: any): boolean {
        const valueToCheck = normalizeValueWithLength($value);
        return typeof valueToCheck !== 'string' || ((!this.min || valueToCheck.length >= this.min) && (!this.max || valueToCheck.length <= this.max));
    }

    public getMessageForValue($value: any): string {
        const valueToCheck = normalizeValueWithLength($value);
        return this.min && valueToCheck.length < this.min ? this.minMessage : this.maxMessage;
    }
}

export class MinLengthRule extends ValidationRuleWithMessage {
    private readonly message: string;

    constructor(
        public readonly length: number,
        $message: string = 'Length should be greater then {{ length }}.'
    ) {
        super(ValidationType.MIN_LENGTH);
        this.message = ValidationRule.plural(
            interpolate($message, this),
            this.length
        );
    }

    public validate($value: any): boolean {
        const valueToCheck = normalizeValueWithLength($value);
        return !valueToCheck || valueToCheck.length >= this.length;
    }

    public getMessageForValue($value: any): string {
        return this.message;
    }
}

export class MaxLengthRule extends ValidationRuleWithMessage {
    private readonly message: string;

    constructor(
        public readonly length: number,
        $message: string = 'Length must not exceed {{ length }}.'
    ) {
        super(ValidationType.MAX_LENGTH);
        this.message = ValidationRule.plural(
            interpolate($message, this),
            this.length
        );
    }

    public validate($value: any): boolean {
        const valueToCheck = normalizeValueWithLength($value);
        return !valueToCheck || valueToCheck.length <= this.length;
    }

    public getMessageForValue($value: any): string {
        return this.message;
    }
}

export class MinOrEqualRule extends ValidationRuleWithMessage {
    protected readonly message: string;

    constructor(
        public readonly limit: number,
        $message: string = 'Value must be greater than or equal to {{ limit }}.'
    ) {
        super(ValidationType.MIN_OR_EQUAL);
        this.message = ValidationRule.plural(
            interpolate($message, this),
            this.limit
        );
    }

    public validate($value: any): boolean {
        return typeof $value === 'number' ? $value >= this.limit : !$value;
    }

    public getMessageForValue($value: any): string {
        return this.message;
    }
}

export class MinRule extends ValidationRuleWithMessage {
    protected readonly message: string;

    constructor(
        public readonly limit: number,
        $message: string = 'Value must be greater than {{ limit }}.'
    ) {
        super(ValidationType.MIN);
        this.message = ValidationRule.plural(
            interpolate($message, this),
            this.limit
        );
    }

    public validate($value: any): boolean {
        return typeof $value === 'number' ? $value > this.limit : !$value;
    }

    public getMessageForValue($value: any): string {
        return this.message;
    }
}

export class MaxOrEqualRule extends ValidationRuleWithMessage {
    private readonly message: string;

    constructor(
        public readonly limit: number,
        $message: string = 'Value must be lower than or equal to {{ limit }}.'
    ) {
        super(ValidationType.MAX_OR_EQUAL);
        this.message = ValidationRule.plural(
            interpolate($message, this),
            this.limit
        );
    }

    public validate($value: any): boolean {
        return typeof $value === 'number' ? $value <= this.limit : !$value;
    }

    public getMessageForValue($value: any): string {
        return this.message;
    }
}

export class MaxRule extends ValidationRuleWithMessage {
    private readonly message: string;

    constructor(
        public readonly limit: number,
        $message: string = 'Value must be lower than {{ limit }}.'
    ) {
        super(ValidationType.MAX);
        this.message = ValidationRule.plural(
            interpolate($message, this),
            this.limit
        );
    }

    public validate($value: any): boolean {
        return typeof $value === 'number' ? $value < this.limit : !$value;
    }

    public getMessageForValue($value: any): string {
        return this.message;
    }
}

export class RequiredRule extends ValidationRuleWithMessage {
    constructor(
        private readonly message: string = 'This field is required.'
    ) {
        super(ValidationType.REQUIRED);
    }

    public validate($value: any): boolean {
        return !!$value;
    }

    public getMessageForValue($value: any): string {
        return this.message;
    }
}

export class NotEmptyRule extends ValidationRuleWithMessage {
    constructor(
        private readonly message: string = 'This field must not be empty.'
    ) {
        super(ValidationType.NOT_EMPTY);
    }

    public validate($value: any): boolean {
        return !!$value && !!('' + $value).trim().length;
    }

    public getMessageForValue($value: any): string {
        return this.message;
    }
}

export class PatternRule extends ValidationRuleWithMessage {
    private readonly message: string;

    constructor(
        public readonly pattern: RegExp,
        public readonly match: boolean = true,
        $message: string = 'Pattern does not match.'
    ) {
        super(ValidationType.PATTERN);
        this.message = interpolate($message, this);
    }

    public validate($value: any): boolean {
        const valueToCheck = normalizeValueAsString($value);
        return !valueToCheck || this.match === this.pattern.test(valueToCheck);
    }

    public getMessageForValue($value: any): string {
        return this.message;
    }
}

export class EmailRule extends ValidationRuleWithMessage {
    // https://github.com/angular/angular/blob/4.4.6/packages/forms/src/validators.ts#L109
    /* tslint:disable:max-line-length */
    // static RE = /^(?=.{1,254}$)(?=.{1,64}@)[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+(\.[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+)*@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;
    // see http://jira.uncomp.com:8989/browse/NS-1636
    static RE = /^(?=.{1,254}$)(?=.{1,64}@)[-'0-9=A-Z_a-z]+(\.[-'0-9=A-Z_a-z]+)*@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;
    /* tslint:enable:max-line-length */
    private readonly message: string;

    constructor(
        public readonly strict: boolean = false,
        $message: string = 'Valid email address must be provided.'
    ) {
        super(ValidationType.EMAIL);
        this.message = interpolate($message, this);
    }

    public validate($value: any): boolean {
        return !$value || EmailRule.RE.test($value);
    }

    public getMessageForValue($value: any): string {
        return this.message;
    }
}

export class IncorrectValueRule extends ValidationRuleWithMessage {
    private readonly message: string;

    constructor(
        public readonly value: any,
        $message: string = 'Value you provided is incorrect.'
    ) {
        super(ValidationType.INCORRECT);
        this.message = interpolate($message, this);
    }

    public validate($value: any): boolean {
        return $value === undefined
            || $value === null
            || (typeof $value !== 'object' && this.value !== $value)
            || (typeof $value === 'object' && !maybeEquals(this.value, $value));
    }

    public getMessageForValue($value: any): string {
        return this.message;
    }
}

export class DigitsRule extends ValidationRuleWithMessage {
    constructor(
        public readonly message: string = 'Value must be a digit.'
    ) {
        super(ValidationType.DIGITS);
    }

    public validate($value: any): boolean {
        return !$value || !!Number($value);
    }

    public getMessageForValue($value: any): string {
        return this.message;
    }
}

export class AllowedValueRule extends ValidationRuleWithMessage {
    constructor(
        public readonly values: ReadonlyArray<any>
    ) {
        super(ValidationType.ALLOWED);
    }

    public validate($value: any): boolean {
        return this.values.indexOf($value) > -1;
    }

    public getMessageForValue($value: any): string {
        return interpolate('Value %s is not allowed here', $value);
    }
}

export class ExternalValidatorRule extends ValidationRule {
    constructor(
        public readonly validatorFn: ($value: any, $formData?: any) => IValidationRuleError | null
    ) {
        super(ValidationType.EXTERNAL);
    }

    public get validator(): ValidatorFn {
        return ($control: AbstractControl): { [key: string]: IValidationRuleError } | null => {
            const result = this.validatorFn($control.value, $control.root.value);
            if (result) {
                return {[this.name]: result};
            }
            return null;
        };
    }
}

export class PhoneRule extends ValidationRuleWithMessage {
    static RE = /^\+?\s?(?:\d\s?){7,13}\d$/;

    constructor(
        private readonly message: string = 'Invalid phone number.'
    ) {
        super(ValidationType.PHONE);
    }

    public validate($value: any): boolean {
        return !$value || PhoneRule.RE.test($value);
    }

    public getMessageForValue($value: any): string {
        return this.message;
    }
}

export class AgeRule extends ValidationRuleWithMessage {
    private readonly message: string;

    constructor(
        public readonly age: number = 18,
        $message: string = 'Age must be more than {{ age }}.'
    ) {
        super(ValidationType.AGE);
        this.message = ValidationRule.plural(
            interpolate($message, this),
            this.age
        );
    }

    public validate($value: Moment | Date | null): boolean {
        if ($value) {
            const currentDate = new Date();

            if (typeof $value === 'string') {
                return (currentDate.getFullYear() - new Date($value).getFullYear()) >= this.age;
            }

            // TODO Vadym remove logic for Moment
            if ('startOf' in $value) {
                return (currentDate.getFullYear() - $value.toDate().getFullYear()) >= this.age;
            }

            if ($value instanceof Date) {
                return (currentDate.getFullYear() - $value.getFullYear()) >= this.age;
            }


        }
        return true;
    }

    public getMessageForValue($value: any): string {
        return this.message;
    }
}

export class BooleanRule extends ValidationRuleWithMessage {
    private readonly message: string;

    public constructor(
        private readonly $message: string = 'This field should be "true" or "false"'
    ) {
        super(ValidationType.BOOLEAN);
        this.message = $message;
    }

    public validate($value: any): boolean {
        return typeof $value === 'boolean';
    }

    public getMessageForValue($value: any): string {
        return this.message;
    }
}
